import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { UserRole } from '@/types/auth';
import { authApi } from '@/api/auth';

export const handleSignUp = async (
  email: string, 
  password: string, 
  role: UserRole = "bizadmin", 
  companyName = "",
  firstName = "", 
  lastName = "",
  priceId?: string
) => {
  const context: ErrorContext = {
    operation: 'handleSignUp',
    additionalInfo: { email, role }
  };

  try {
    console.log(`handleSignUp: Starting signup process with role ${role}`);
    
    // Use the new API service to handle the entire signup process
    const response = await authApi.signUp(
      email, 
      password, 
      role, 
      companyName, 
      firstName, 
      lastName,
      priceId
    );
    
    // Check for errors
    if (response.status === 'error' || response.error) {
      console.error("handleSignUp: Error from authApi.signUp:", response.error);
      console.error("handleSignUp: Error message:", response.message);
      
      return handleError(
        response.error,
        response.message || 'Error during signup process',
        ErrorSeverity.ERROR,
        context,
        true
      ) && { user: null, error: response.error };
    }
    
    console.log("handleSignUp: Success! User created:", response.data?.user?.id);
    
    // Return success with the user data
    return { 
      user: response.data?.user, 
      error: null,
      profile: response.data?.profile,
      organization: response.data?.organization
    };
  } catch (error) {
    console.error("handleSignUp: Unexpected catch block error:", error);
    return handleError(
      error,
      "Unexpected error during sign up",
      ErrorSeverity.ERROR,
      context,
      true
    ) && { user: null, error };
  }
};

export const handleSignIn = async (email: string, password: string) => {
  const context: ErrorContext = {
    operation: 'handleSignIn',
    additionalInfo: { email }
  };

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      handleError(
        error,
        'Error during sign in',
        ErrorSeverity.ERROR,
        context,
        true
      );
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    handleError(
      error,
      "Error during sign in",
      ErrorSeverity.ERROR,
      context,
      true
    );
    return { data: null, error };
  }
};

export const handleSignOut = async () => {
  const context: ErrorContext = {
    operation: 'handleSignOut'
  };

  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      handleError(
        error,
        'Error during sign out',
        ErrorSeverity.ERROR,
        context,
        true
      );
    }
    
    return { error };
  } catch (error) {
    handleError(
      error,
      "Error during sign out",
      ErrorSeverity.ERROR,
      context,
      true
    );
    return { error };
  }
};

// Define allowed self signup roles
export const allowedSelfSignupRoles: UserRole[] = ['bizadmin', 'parent'];
